import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { GenreSelector, parseGenres, formatGenres } from "@/components/ui/genre-selector";
import { Badge } from "@/components/ui/badge";
import { Plus, Key, Upload, Check, X, Image, X as XIcon, Trash2, Calendar, ExternalLink, RefreshCw, CheckCircle, AlertTriangle, Star, AlertCircle, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { addReport, removeReport, hasDeveloperReportedCreator, getCreatorReportDetails, formatReportDate, getCreatorTrustScore, getTrustScoreEmoji, getTrustScoreColor, getTrustScoreLabel } from "@/lib/reportUtils";
import { getGameDetails } from "@/services/steamService";
import { GAME_GENRES } from "@/data/gameGenres";
import { AccountNotVerified } from "@/components/AccountNotVerified";
import { generatePressKit, checkPressKitExists } from "@/services/pressKitService";

interface Game {
  id: string;
  title: string;
  description: string;
  genres: string[];
  cover_image_url: string;
  screenshots: string[];
  steam_url: string;
  price: number;
  available_keys: number;
  total_keys: number;
  available_public_keys?: number;
  available_creator_keys?: number;
  pending_requests: number;
  created_at: string;
  release_date?: string;
  highlight_id: number;
  highlight_end_date?: string;
  hashtag?: string;
  youtube_tag?: string;
  twitch_category?: string;
  keymailer_hashtag?: string;
  disclosure_text?: string;
  is_published: boolean;
  allow_requests: boolean;
  hasPressKit?: boolean;
  developer?: {
    display_name: string;
    studio_name?: string;
    studio_logo_url?: string;
  };
}

interface KeyRequest {
  id: string;
  game_id: string;
  creator_id: string;
  created_at: string;
  message: string;
  status: string;
  response_message?: string;
  game: { title: string };
  creator: {
    id: string;
    display_name: string;
    avatar_url?: string;
    bio?: string;
    platform?: string;
    channel_url?: string;
    subscriber_count?: number;
    favorite_genres?: string;
    twitch_url?: string;
    youtube_url?: string;
    tiktok_url?: string;
    instagram_url?: string;
    twitter_url?: string;
    bluesky_url?: string;
    has_reported?: boolean;
    report_details?: {
      total_reports: number;
      latest_report_date: string | null;
    };
    trust_score?: number;
  };
}

export default function DeveloperDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [isAddingGame, setIsAddingGame] = useState(false);
  const [isSteamSyncModal, setIsSteamSyncModal] = useState(false);
  const [steamGameIdInput, setSteamGameIdInput] = useState("");
  const [isLoadingSteamData, setIsLoadingSteamData] = useState(false);
  const [newGame, setNewGame] = useState({
    title: "",
    description: "",
    genres: [] as string[],
    cover_image_url: "",
    screenshots: [] as string[],
    steam_url: "",
    steam_game_id: "",
    price: 0,
    release_date: "",
    highlight_id: 1,
    hashtag: "",
    youtube_tag: "",
    twitch_category: "",
    keymailer_hashtag: "#indiekeys",
    disclosure_text: "Disclosure: I received a free review copy of this product from IndieKeys",
    youtube_trailer_url: ""
  });
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [isManagingKeys, setIsManagingKeys] = useState(false);
  const [newKeys, setNewKeys] = useState<string>("");
  const [isAddingKeys, setIsAddingKeys] = useState(false);
  const [keysForPublicPlayers, setKeysForPublicPlayers] = useState(false);
  const [isViewingRequests, setIsViewingRequests] = useState(false);
  const [gameRequests, setGameRequests] = useState<KeyRequest[]>([]);
  const [selectedRequester, setSelectedRequester] = useState<KeyRequest | null>(null);
  const [isViewingCreatorProfile, setIsViewingCreatorProfile] = useState(false);
  const [isSyncingKeys, setIsSyncingKeys] = useState(false);
  const [autoSyncCompleted, setAutoSyncCompleted] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGeneratingPressKit, setIsGeneratingPressKit] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [existingPublicKeysCount, setExistingPublicKeysCount] = useState<number>(0);
  const [existingCreatorKeysCount, setExistingCreatorKeysCount] = useState<number>(0);

  useEffect(() => {
    if (user?.id) {
      checkVerificationStatus();
      checkAdminAccess();
      checkPremiumStatus();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && isVerified === true) {
      fetchGames();
      // Auto-sync keys when dashboard loads (silent)
      syncAvailableKeys(false);
    }
  }, [user?.id, isVerified]);

  const checkVerificationStatus = async () => {
    if (!user?.id) return;

    setIsCheckingVerification(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_verified")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error checking verification status:", error);
        setIsVerified(false);
      } else {
        setIsVerified(data?.is_verified === true);
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setIsVerified(false);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const checkAdminAccess = async () => {
    if (!user?.id) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      // Fallback: allow miikeu user even if there's a DB error
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      }
    }
  };

  const checkPremiumStatus = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_premium, premium_end_date")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error checking premium status:", error);
        setIsPremium(false);
        return;
      }

      const hasPremium = data?.is_premium === true;
      if (hasPremium) {
        const premiumEndDate = data?.premium_end_date;
        // Si pas de date de fin, c'est premium à vie
        if (!premiumEndDate) {
          setIsPremium(true);
        } else {
          // Vérifier que la date n'est pas expirée
          const endDate = new Date(premiumEndDate);
          const now = new Date();
          setIsPremium(endDate > now);
        }
      } else {
        setIsPremium(false);
      }
    } catch (error) {
      console.error("Error checking premium status:", error);
      setIsPremium(false);
    }
  };

  const handleGeneratePressKit = async (gameId: string) => {
    if (!user?.id) return;

    setIsGeneratingPressKit(gameId);
    try {
      const result = await generatePressKit(gameId, user.id);
      if (result.success) {
        toast.success("Press kit generated successfully!");
        // Rafraîchir la liste des jeux pour mettre à jour le statut hasPressKit
        await fetchGames();
      } else {
        toast.error(result.error || "Failed to generate press kit");
      }
    } catch (error) {
      console.error("Error generating press kit:", error);
      toast.error("Error generating press kit");
    } finally {
      setIsGeneratingPressKit(null);
    }
  };

  const fetchGames = async () => {
    const { data } = await supabase
      .from("games")
      .select(`
        id,
        title,
        description,
        genre,
        cover_image_url,
        screenshots,
        steam_url,
        price,
        total_keys,
        available_keys,
        is_published,
        allow_requests,
        created_at,
        updated_at,
        release_date,
        highlight_id,
        highlight_end_date,
        hashtag,
        youtube_tag,
        twitch_category,
        keymailer_hashtag,
        disclosure_text,
        key_requests(
          id,
          status
        ),
        developer:profiles!games_developer_id_fkey(
          display_name,
          studio_name,
          studio_logo_url,
          studio_slug
        )
      `)
      .eq("developer_id", user?.id)
      .order("created_at", { ascending: false });

    // Pour chaque jeu, récupérer le nombre de clés publiques et créateurs disponibles
    const processedGames = [];
    for (const game of (data || [])) {
      const pendingRequests = (game as any).key_requests?.filter((req: any) => req.status === 'pending').length || 0;

      // Compter les clés publiques disponibles
      const { count: publicKeysCount } = await supabase
        .from("keys")
        .select("*", { count: 'exact', head: true })
        .eq("game_id", (game as any).id)
        .eq("is_claimed", false)
        .eq("public", true);

      // Compter les clés créateurs disponibles
      const { count: creatorKeysCount } = await supabase
        .from("keys")
        .select("*", { count: 'exact', head: true })
        .eq("game_id", (game as any).id)
        .eq("is_claimed", false)
        .eq("public", false);

      // Vérifier si un press kit existe pour ce jeu
      const hasPressKit = await checkPressKitExists((game as any).id);

      const gameData = game as any;
      processedGames.push({
        id: gameData.id,
        title: gameData.title,
        description: gameData.description,
        cover_image_url: gameData.cover_image_url,
        screenshots: gameData.screenshots || [],
        steam_url: gameData.steam_url,
        price: gameData.price || 0,
        total_keys: gameData.total_keys || 0,
        available_keys: gameData.available_keys || 0,
        created_at: gameData.created_at,
        release_date: gameData.release_date,
        genres: parseGenres(gameData.genre || ""), // Parser les genres depuis la base de données
        pending_requests: pendingRequests,
        highlight_id: gameData.highlight_id || 1,
        highlight_end_date: gameData.highlight_end_date || null,
        is_published: gameData.is_published || false,
        allow_requests: gameData.allow_requests !== false,
        available_public_keys: publicKeysCount || 0,
        available_creator_keys: creatorKeysCount || 0,
        hashtag: gameData.hashtag,
        youtube_tag: gameData.youtube_tag,
        twitch_category: gameData.twitch_category,
        keymailer_hashtag: gameData.keymailer_hashtag,
        disclosure_text: gameData.disclosure_text,
        hasPressKit: hasPressKit,
        developer: gameData.developer
      });
    }

    setGames(processedGames);
  };

  const syncAvailableKeys = async (showToast = true) => {
    if (showToast) {
      setIsSyncingKeys(true);
    }

    try {
      // Pour chaque jeu, compter les clés non réclamées
      for (const game of games) {
        const { data: unclaimedKeys, error: keysError } = await supabase
          .from("keys")
          .select("id")
          .eq("game_id", game.id)
          .eq("is_claimed", false);

        if (keysError) {
          console.error(`Error fetching keys for game ${game.id}:`, keysError);
          continue;
        }

        const actualAvailableKeys = unclaimedKeys?.length || 0;

        // Mettre à jour si différent
        if (actualAvailableKeys !== game.available_keys) {
          const { error: updateError } = await supabase
            .from("games")
            .update({ available_keys: actualAvailableKeys })
            .eq("id", game.id);

          if (updateError) {
            console.error(`Error updating available_keys for game ${game.id}:`, updateError);
          }
        }
      }

      if (showToast) {
        toast.success("Available keys synchronized!");
      } else {
        setAutoSyncCompleted(true);
        // Hide the auto-sync indicator after 3 seconds
        setTimeout(() => setAutoSyncCompleted(false), 3000);
      }
      fetchGames(); // Refresh the games list
    } catch (error) {
      console.error("Error:", error);
      if (showToast) {
        toast.error("An error occurred");
      }
    } finally {
      if (showToast) {
        setIsSyncingKeys(false);
      }
    }
  };

  // Function to map Steam genres to application genres
  const mapSteamGenresToAppGenres = (steamGenres: any[]): string[] => {
    if (!steamGenres || !Array.isArray(steamGenres)) return [];

    const genreMap: { [key: string]: string | string[] } = {
      // Direct matches
      "Action": "Action",
      "Free To Play": "Free-to-Play",
      "Adventure": "Adventure",
      "RPG": "RPG",
      "Strategy": "Real-Time Strategy (RTS)",
      "Simulation": "Simulation",
      "Sports": "Sports",
      "Racing": "Racing",
      "Indie": "Indie",
      "Casual": "Casual",
      "Early Access": "Early Access",

      // Action subgenres
      "Action RPG": "Action RPG",
      "Hack and Slash": "Hack and Slash",
      "Beat 'em up": "Beat 'em up",
      "Fighting": "Fighting",
      "Stealth": "Stealth",
      "Survival": "Survival",
      "Survival Horror": "Survival Horror",
      "Platformer": "Platformer",

      // Shooter genres
      "FPS": "First-Person Shooter (FPS)",
      "First-Person Shooter": "First-Person Shooter (FPS)",
      "Third-Person Shooter": "Third-Person Shooter (TPS)",
      "TPS": "Third-Person Shooter (TPS)",
      "Tactical Shooter": "Tactical Shooter",
      "Battle Royale": "Battle Royale",
      "Hero Shooter": "Hero Shooter",

      // Strategy subgenres
      "Real-Time Strategy": "Real-Time Strategy (RTS)",
      "RTS": "Real-Time Strategy (RTS)",
      "Turn-Based Strategy": "Turn-Based Strategy",
      "Tower Defense": "Tower Defense",
      "4X": "4X Strategy",
      "4X Strategy": "4X Strategy",
      "Grand Strategy": "Grand Strategy",
      "City Builder": "City Builder",
      "Management": "Management",

      // Simulation subgenres
      "Life Simulation": "Life Simulation",
      "Vehicle Simulation": "Vehicle Simulation",
      "Flight Simulation": "Flight Simulation",
      "Space Simulation": "Space Simulation",
      "Farming Simulation": "Farming Simulation",
      "Business Simulation": "Business Simulation",
      "Sports Simulation": "Sports Simulation",

      // RPG subgenres
      "JRPG": "JRPG",
      "Western RPG": "Western RPG",
      "Roguelike": "Roguelike",
      "Roguelite": "Roguelite",
      "Dungeon Crawler": "Dungeon Crawler",
      "MMORPG": "MMORPG",
      "Open-World RPG": "Open-World RPG",

      // Sports subgenres
      "Football": "Football/Soccer",
      "Soccer": "Football/Soccer",
      "Basketball": "Basketball",
      "Tennis": "Tennis",
      "Golf": "Golf",
      "Baseball": "Baseball",
      "Hockey": "Hockey",
      "Wrestling": "Wrestling",
      "Boxing": "Boxing",
      "Martial Arts": "Martial Arts",
      "Extreme Sports": "Extreme Sports",

      // Puzzle & Casual
      "Puzzle": "Puzzle",
      "Match-3": "Match-3",
      "Hidden Object": "Hidden Object",
      "Point & Click": "Point & Click",
      "Visual Novel": "Visual Novel",
      "Interactive Fiction": "Interactive Fiction",
      "Educational": "Educational",
      "Trivia": "Trivia",
      "Card Game": "Card Game",
      "Board Game": "Board Game",
      "Party Game": "Party Game",

      // Sandbox & Creation
      "Sandbox": "Sandbox",
      "Open World": "Open World",
      "Crafting": "Crafting",
      "Building": "Building",
      "Creative": "Creative",
      "Art Game": "Art Game",

      // Multiplayer
      "Multiplayer": "Multiplayer",
      "Co-op": "Co-op",
      "Cooperative": "Co-op",
      "Competitive": "Competitive",
      "Team-Based": "Team-Based",
      "Massively Multiplayer": "Massively Multiplayer",
      "MMO": "Massively Multiplayer",
      "Local Multiplayer": "Local Multiplayer",
      "Online Multiplayer": "Online Multiplayer",

      // Indie & Niche
      "Retro": "Retro",
      "Pixel Art": "Pixel Art",
      "2D": "2D",
      "3D": "3D",
      "Side-Scroller": "Side-Scroller",
      "Top-Down": "Top-Down",
      "Isometric": "Isometric",

      // Thematic
      "Horror": "Horror",
      "Psychological Horror": "Psychological Horror",
      "Sci-Fi": "Sci-Fi",
      "Science Fiction": "Sci-Fi",
      "Fantasy": "Fantasy",
      "Medieval": "Medieval",
      "Historical": "Historical",
      "Post-Apocalyptic": "Post-Apocalyptic",
      "Cyberpunk": "Cyberpunk",
      "Steampunk": "Steampunk",
      "Western": "Western",
      "Noir": "Noir",
      "Mystery": "Mystery",
      "Thriller": "Thriller",
      "Drama": "Drama",
      "Comedy": "Comedy",
      "Romance": "Romance",

      // Special mechanics
      "Metroidvania": "Metroidvania",
      "Souls-like": "Souls-like",
      "Twin-Stick Shooter": "Twin-Stick Shooter",
      "Bullet Hell": "Bullet Hell",
      "Rhythm": "Rhythm",
      "Music": "Music",
      "Dance": "Dance",
      "Fitness": "Fitness",
      "VR": "VR",
      "Virtual Reality": "VR",
      "Augmented Reality": "Augmented Reality",

      // Platform & Style
      "Mobile": "Mobile",
      "Browser Game": "Browser Game",
      "Beta": "Beta",
      "Demo": "Demo",
      "Mod": "Mod",
      "Total Conversion": "Total Conversion",

      // Other
      "Hardcore": "Hardcore",
      "Narrative": "Narrative",
      "Story Rich": "Story Rich",
      "Character Customization": "Character Customization",
      "Character Progression": "Character Progression",
      "Skill-Based": "Skill-Based",
      "Time Management": "Time Management",
      "Resource Management": "Resource Management",
      "Trading": "Trading",
      "Economy": "Economy",
      "Diplomacy": "Diplomacy",
      "Warfare": "Warfare",
      "Exploration": "Exploration",
      "Discovery": "Discovery",
      "Collection": "Collection",
      "Achievement Hunting": "Achievement Hunting",
      "Speedrun": "Speedrun",
      "Challenge": "Challenge",
      "Relaxing": "Relaxing",
      "Meditative": "Meditative",
      "Atmospheric": "Atmospheric",
      "Immersive": "Immersive",
      "Cinematic": "Cinematic",
      "Theatrical": "Theatrical",
      "Experimental": "Experimental",
      "Abstract": "Abstract",
      "Minimalist": "Minimalist",
      "Maximalist": "Maximalist",
      "Dystopian": "Dystopian",
      "Procedural Generation": "Procedural Generation",
      "Adult": "Adult",
      "Chill": "Chill",
      "Cosy": "Cosy",
      "Cute": "Cute",
      "Pirate": "Pirate",
      "Animal": "Animal",
      "Zombie": "Zombie",
      "Alien": "Alien",
      "Vampire": "Vampire",
      "Werewolf": "Werewolf",
      "Wizard": "Wizard",
    };

    const mappedGenres: string[] = [];
    steamGenres.forEach((genre: any) => {
      const description = genre.description || "";
      if (!description) return;

      // Check direct mapping first
      const mapped = genreMap[description];
      if (mapped) {
        if (Array.isArray(mapped)) {
          mappedGenres.push(...mapped);
        } else {
          mappedGenres.push(mapped);
        }
        return;
      }

      // Try case-insensitive match
      const lowerDescription = description.toLowerCase();
      const caseInsensitiveMatch = Object.entries(genreMap).find(([key]) =>
        key.toLowerCase() === lowerDescription
      );
      if (caseInsensitiveMatch) {
        const mapped = caseInsensitiveMatch[1];
        if (Array.isArray(mapped)) {
          mappedGenres.push(...mapped);
        } else {
          mappedGenres.push(mapped);
        }
        return;
      }

      // Try partial matching with GAME_GENRES
      const matchingGenre = GAME_GENRES.find(g => {
        const lowerG = g.toLowerCase();
        const lowerD = lowerDescription;
        return lowerG === lowerD ||
          lowerG.includes(lowerD) ||
          lowerD.includes(lowerG) ||
          lowerG.replace(/[^a-z0-9]/g, '') === lowerD.replace(/[^a-z0-9]/g, '');
      });
      if (matchingGenre) {
        mappedGenres.push(matchingGenre);
        return;
      }

      // Try fuzzy matching - check if any word matches
      const descriptionWords = lowerDescription.split(/[\s-]+/);
      const fuzzyMatch = GAME_GENRES.find(g => {
        const lowerG = g.toLowerCase();
        return descriptionWords.some(word =>
          word.length > 3 && lowerG.includes(word)
        );
      });
      if (fuzzyMatch) {
        mappedGenres.push(fuzzyMatch);
      }
    });

    return [...new Set(mappedGenres)];
  };

  const handleSteamSync = async () => {
    if (!steamGameIdInput.trim()) {
      toast.error("Please enter a Steam Game ID");
      return;
    }

    setIsLoadingSteamData(true);

    try {
      const steamData = await getGameDetails(Number(steamGameIdInput));

      if (!steamData) {
        toast.error("Failed to fetch game data from Steam");
        setIsLoadingSteamData(false);
        return;
      }

      // Debug: log les données Steam pour vérifier la structure
      console.log("Steam Data received:", {
        name: steamData.name,
        is_free: steamData.is_free,
        price_overview: steamData.price_overview,
        package_groups: steamData.package_groups,
        release_date: steamData.release_date,
        detailed_description: steamData.detailed_description?.substring(0, 100),
        genres: steamData.genres,
        header_image: steamData.header_image,
        screenshots_count: steamData.screenshots?.length
      });

      // Mapper les données Steam aux champs du formulaire
      const mappedGenres = mapSteamGenresToAppGenres(steamData.genres || []);

      // Extraire le prix depuis price_overview (méthode principale) ou package_groups (fallback)
      let price = 0;
      if (steamData.is_free) {
        price = 0;
      } else if (steamData.price_overview) {
        // Utiliser price_overview si disponible (plus fiable)
        const finalPrice = steamData.price_overview.final || steamData.price_overview.final_formatted;
        if (steamData.price_overview.final) {
          // final est en centimes
          price = steamData.price_overview.final / 100;
        } else if (steamData.price_overview.initial) {
          price = steamData.price_overview.initial / 100;
        }
      } else if (steamData.package_groups && steamData.package_groups.length > 0) {
        const firstGroup = steamData.package_groups[0];
        if (firstGroup.subs && firstGroup.subs.length > 0) {
          // Chercher le premier package payant
          const paidPackage = firstGroup.subs.find((p: any) => !p.is_free_license && p.price_in_cents_with_discount > 0);
          if (paidPackage) {
            // Convertir les centimes en euros (ou autre devise selon le besoin)
            price = paidPackage.price_in_cents_with_discount / 100;
          }
        }
      }

      // Extraire les screenshots
      const screenshots = (steamData.screenshots || []).map((s: any) => s.path_full || s.path_thumbnail).filter(Boolean);

      // Formater la date de sortie
      let releaseDate = "";
      if (steamData.release_date && steamData.release_date.date) {
        try {
          const date = new Date(steamData.release_date.date);
          if (!isNaN(date.getTime())) {
            releaseDate = date.toISOString().split('T')[0]; // Format YYYY-MM-DD
          }
        } catch (e) {
          console.warn("Could not parse release date:", steamData.release_date.date);
        }
      }

      // Construire l'URL Steam si on a le steam_appid
      const steamUrl = steamData.steam_appid
        ? `https://store.steampowered.com/app/${steamData.steam_appid}`
        : "";

      // Mettre à jour newGame avec les données Steam (identique à GameDetail)
      setNewGame({
        title: steamData.name || "",
        description: steamData.detailed_description || steamData.about_the_game || steamData.short_description || "",
        genres: mappedGenres.length > 0 ? mappedGenres : [],
        cover_image_url: steamData.header_image || "",
        screenshots: screenshots,
        steam_url: steamUrl,
        steam_game_id: steamGameIdInput,
        price: price,
        release_date: releaseDate,
        highlight_id: 1,
        hashtag: "",
        youtube_tag: "",
        twitch_category: "",
        keymailer_hashtag: "#indiekeys",
        disclosure_text: "Disclosure: I received a free review copy of this product from IndieKeys"
      });

      // Fermer la modal Steam et ouvrir la modal complète
      setIsSteamSyncModal(false);
      setIsAddingGame(true);

      toast.success("Steam data synced! Please review and complete the form.");
    } catch (error) {
      console.error('Error fetching Steam data:', error);
      toast.error("Error fetching Steam data");
    } finally {
      setIsLoadingSteamData(false);
    }
  };

  const handleAddGame = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let coverImageUrl = "";
      let screenshotUrls: string[] = [];

      // Upload cover image (si fichier fourni, sinon utiliser l'URL Steam si disponible)
      if (coverImageFile) {
        const coverPath = `covers/${user?.id}/${Date.now()}-${coverImageFile.name}`;
        coverImageUrl = await uploadImage(coverImageFile, coverPath);
      } else if (newGame.cover_image_url) {
        // Utiliser l'image Steam si pas de fichier uploadé
        coverImageUrl = newGame.cover_image_url;
      }

      // Upload screenshots (si fichiers fournis, sinon utiliser les URLs Steam si disponibles)
      if (screenshotFiles.length > 0) {
        const uploadPromises = screenshotFiles.map(async (file, index) => {
          const screenshotPath = `screenshots/${user?.id}/${Date.now()}-${index}-${file.name}`;
          return await uploadImage(file, screenshotPath);
        });
        screenshotUrls = await Promise.all(uploadPromises);
      } else if (newGame.screenshots && newGame.screenshots.length > 0) {
        // Utiliser les screenshots Steam si pas de fichiers uploadés
        screenshotUrls = newGame.screenshots;
      }

      // Insert game with uploaded image URLs
      const { title, description, steam_url, steam_game_id, price, release_date, highlight_id, hashtag, twitch_category, keymailer_hashtag, disclosure_text, youtube_trailer_url } = newGame;
      const { error } = await supabase
        .from("games")
        .insert({
          title,
          description,
          genre: formatGenres(newGame.genres), // Convertir le tableau en string séparée par virgules
          cover_image_url: coverImageUrl,
          screenshots: screenshotUrls,
          steam_url,
          steam_game_id: steam_game_id ? parseInt(steam_game_id) : null,
          price,
          release_date,
          highlight_id,
          hashtag,
          twitch_category,
          keymailer_hashtag,
          disclosure_text,
          youtube_trailer_url: youtube_trailer_url || null,
          developer_id: user?.id
        });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Game added successfully!");
        setIsAddingGame(false);
        setIsSteamSyncModal(false);
        setSteamGameIdInput("");
        setNewGame({
          title: "",
          description: "",
          genres: [],
          cover_image_url: "",
          screenshots: [],
          steam_url: "",
          steam_game_id: "",
          price: 0,
          release_date: "",
          highlight_id: 1,
          hashtag: "",
          youtube_tag: "",
          twitch_category: "",
          keymailer_hashtag: "#indiekeys",
          disclosure_text: "Disclosure: I received a free review copy of this product from IndieKeys"
        });
        setCoverImageFile(null);
        setScreenshotFiles([]);
        fetchGames();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Error uploading images");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRequestAction = async (requestId: string, status: "approved" | "rejected") => {
    try {
      if (status === "approved") {
        // Trouver la requête pour obtenir les informations nécessaires
        const request = gameRequests.find(r => r.id === requestId);
        if (!request) {
          toast.error("Request not found");
          return;
        }

        // Déterminer le type de clé à utiliser selon le user_type du requester
        const { data: requesterProfile } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", request.creator_id)
          .single();

        const isPlayer = (requesterProfile as any)?.user_type === 'player';

        // Trouver une clé disponible pour ce jeu (publique pour players, créateur pour creators)
        const keysQuery = supabase
          .from("keys")
          .select("id, key_code")
          .eq("game_id", request.game_id)
          .eq("is_claimed", false)
          .eq("public", isPlayer ? true : false)
          .limit(1);

        const { data: availableKeys, error: keysError } = await keysQuery;

        if (keysError) {
          console.error("Error fetching available keys:", keysError);
          toast.error("Failed to fetch available keys");
          return;
        }

        if (!availableKeys || availableKeys.length === 0) {
          toast.error("No available keys for this game");
          return;
        }

        const selectedKey = availableKeys[0];

        // Marquer la clé comme réclamée
        const { error: claimError } = await supabase
          .from("keys")
          .update({
            is_claimed: true,
            claimed_by: request.creator_id,
            claimed_at: new Date().toISOString()
          })
          .eq("id", selectedKey.id);

        if (claimError) {
          console.error("Error claiming key:", claimError);
          toast.error("Failed to claim key");
          return;
        }

        // Mettre à jour le statut de la requête avec la clé
        const { error: updateError } = await supabase
          .from("key_requests")
          .update({
            status,
            response_message: `Key assigned: ${selectedKey.key_code}`
          })
          .eq("id", requestId);

        if (updateError) {
          console.error("Error updating request:", updateError);
          toast.error("Failed to update request");
          return;
        }

        // Décrémenter le compteur available_keys dans la table games
        // D'abord, récupérer la valeur actuelle
        const { data: currentGame, error: fetchError } = await supabase
          .from("games")
          .select("available_keys")
          .eq("id", request.game_id)
          .single();

        if (fetchError) {
          console.error("Error fetching current available_keys:", fetchError);
        } else if (currentGame && currentGame.available_keys > 0) {
          const { error: updateGameError } = await supabase
            .from("games")
            .update({
              available_keys: currentGame.available_keys - 1
            })
            .eq("id", request.game_id);

          if (updateGameError) {
            console.error("Error updating game available_keys:", updateGameError);
            // Ne pas faire échouer l'opération pour ça, juste logger
          }
        }

        toast.success("Request approved and key assigned!");
      } else {
        // Pour les requêtes rejetées, juste mettre à jour le statut
        const { error } = await supabase
          .from("key_requests")
          .update({ status })
          .eq("id", requestId);

        if (error) {
          toast.error(error.message);
          return;
        }

        toast.success(`Request ${status}!`);
      }

      // Rafraîchir les demandes du jeu et la liste des jeux
      if (selectedGame) {
        handleViewRequests(selectedGame);
      }
      fetchGames();
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };

  const handleReportCreator = async (creatorId: string, isReporting: boolean) => {
    if (!user?.id) return;

    try {
      if (isReporting) {
        await addReport(user.id, creatorId);
        toast.success('Creator reported successfully');
      } else {
        await removeReport(user.id, creatorId);
        toast.success('Creator unreported successfully');
      }
      if (selectedGame) {
        handleViewRequests(selectedGame);
      }
    } catch (error) {
      console.error('Error reporting creator:', error);
      toast.error('Failed to report creator');
    }
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const { data, error } = await supabase.storage
      .from('game-images')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw error;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('game-images')
      .getPublicUrl(data.path);

    return publicUrl;
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setCoverImageFile(file);
    }
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    setScreenshotFiles(prev => [...prev, ...validFiles]);
  };

  const removeScreenshotFile = (index: number) => {
    setScreenshotFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGameClick = async (game: Game) => {
    setSelectedGame(game);
    setIsManagingKeys(true);
    setNewKeys("");
    // Reset counters first
    setExistingPublicKeysCount(0);
    setExistingCreatorKeysCount(0);

    // Load existing key counts for this game
    if (game.id) {
      const { count: existingPublicKeys } = await supabase
        .from("keys")
        .select("*", { count: 'exact', head: true })
        .eq("game_id", game.id)
        .eq("public", true);

      const { count: existingCreatorKeys } = await supabase
        .from("keys")
        .select("*", { count: 'exact', head: true })
        .eq("game_id", game.id)
        .eq("public", false);

      setExistingPublicKeysCount(existingPublicKeys || 0);
      setExistingCreatorKeysCount(existingCreatorKeys || 0);
    }
  };

  // Steam key format validation function
  const isValidSteamKey = (key: string): boolean => {
    // Steam format: groups of 4-5 alphanumeric characters separated by hyphens
    // Valid examples: XXXX-XXXX-XXXX, XXXX-XXXX-XXXX-XXXX, XXXXX-XXXXX-XXXXX, etc.
    // At least 3 groups, each group contains 4-5 alphanumeric characters
    const steamKeyPattern = /^[A-Z0-9]{4,5}(-[A-Z0-9]{4,5}){2,}$/i;
    return steamKeyPattern.test(key.trim());
  };

  // Check if all keys are valid
  const areAllKeysValid = (keysString: string): boolean => {
    if (!keysString.trim()) return false;
    const keys = keysString
      .split('\n')
      .map(key => key.trim())
      .filter(key => key.length > 0);

    if (keys.length === 0) return false;

    return keys.every(key => isValidSteamKey(key));
  };

  // Handle keys input change with real-time validation
  const handleKeysChange = (value: string) => {
    const upperValue = value.toUpperCase();

    // If premium, no limit
    if (isPremium) {
      setNewKeys(upperValue);
      return;
    }

    // Parse keys from input
    const keys = upperValue
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    // Check limit based on key type
    const maxAllowed = 15;
    const existingCount = keysForPublicPlayers ? existingPublicKeysCount : existingCreatorKeysCount;
    const remainingSlots = maxAllowed - existingCount;

    // If trying to add more than allowed, limit to remaining slots
    if (keys.length > remainingSlots) {
      // Keep only the first 'remainingSlots' keys
      const limitedKeys = keys.slice(0, remainingSlots);
      const limitedValue = limitedKeys.join('\n') + (upperValue.endsWith('\n') ? '\n' : '');
      setNewKeys(limitedValue);
      toast.error(`Maximum ${maxAllowed} ${keysForPublicPlayers ? 'public' : 'creator'} keys allowed. You can only add ${remainingSlots} more.`, {
        duration: 3000
      });
    } else {
      setNewKeys(upperValue);
    }
  };

  const handleAddKeys = async () => {
    if (!selectedGame || !newKeys.trim()) return;

    setIsAddingKeys(true);

    try {
      // Parse keys (one per line)
      const keys = newKeys
        .split('\n')
        .map(key => key.trim())
        .filter(key => key.length > 0);

      if (keys.length === 0) {
        toast.error("Please enter at least one key");
        setIsAddingKeys(false);
        return;
      }

      // Validate the format of each key
      const invalidKeys: string[] = [];
      keys.forEach((key, index) => {
        if (!isValidSteamKey(key)) {
          invalidKeys.push(key);
        }
      });

      // Additional validation check (in case form was submitted somehow)
      if (invalidKeys.length > 0) {
        toast.error(
          `${invalidKeys.length} invalid key(s). Expected format: XXXX-XXXX-XXXX or similar.`,
          {
            description: `Invalid keys: ${invalidKeys.slice(0, 3).join(', ')}${invalidKeys.length > 3 ? '...' : ''}`
          }
        );
        setIsAddingKeys(false);
        return;
      }

      // Check premium status and limit keys for non-premium studios
      if (!isPremium) {
        // Count existing keys for this game
        const { count: existingPublicKeys } = await supabase
          .from("keys")
          .select("*", { count: 'exact', head: true })
          .eq("game_id", selectedGame.id)
          .eq("public", true);

        const { count: existingCreatorKeys } = await supabase
          .from("keys")
          .select("*", { count: 'exact', head: true })
          .eq("game_id", selectedGame.id)
          .eq("public", false);

        const currentPublicKeys = existingPublicKeys || 0;
        const currentCreatorKeys = existingCreatorKeys || 0;

        // Check limit based on key type
        if (keysForPublicPlayers) {
          // Adding public keys
          if (currentPublicKeys >= 15) {
            toast.error("Limit reached: Maximum 15 public keys allowed for non-premium studios");
            setIsAddingKeys(false);
            return;
          }
          const remainingPublicSlots = 15 - currentPublicKeys;
          if (keys.length > remainingPublicSlots) {
            toast.error(`You can only add ${remainingPublicSlots} more public key(s). Maximum 15 public keys allowed for non-premium studios.`);
            setIsAddingKeys(false);
            return;
          }
        } else {
          // Adding creator keys
          if (currentCreatorKeys >= 15) {
            toast.error("Limit reached: Maximum 15 creator keys allowed for non-premium studios");
            setIsAddingKeys(false);
            return;
          }
          const remainingCreatorSlots = 15 - currentCreatorKeys;
          if (keys.length > remainingCreatorSlots) {
            toast.error(`You can only add ${remainingCreatorSlots} more creator key(s). Maximum 15 creator keys allowed for non-premium studios.`);
            setIsAddingKeys(false);
            return;
          }
        }
      }

      // Insérer les clés dans la base de données
      const { error } = await supabase
        .from("keys")
        .insert(
          keys.map(key => ({
            game_id: selectedGame.id,
            key_code: key,
            public: keysForPublicPlayers
          }))
        );

      if (error) {
        toast.error(error.message);
        return;
      }

      // Mettre à jour les compteurs dans la table games
      const { error: updateError } = await supabase
        .from("games")
        .update({
          total_keys: selectedGame.total_keys + keys.length,
          available_keys: selectedGame.available_keys + keys.length
        })
        .eq("id", selectedGame.id);

      if (updateError) {
        toast.error(updateError.message);
        return;
      }

      toast.success(`${keys.length} key(s) added successfully!`);
      setIsManagingKeys(false);
      setSelectedGame(null);
      setNewKeys("");
      setKeysForPublicPlayers(false);
      fetchGames(); // Rafraîchir la liste des jeux

    } catch (error) {
      console.error('Error adding keys:', error);
      toast.error("Error adding keys");
    } finally {
      setIsAddingKeys(false);
    }
  };

  const handleViewRequests = async (game: Game) => {
    setSelectedGame(game);
    setIsViewingRequests(true);
    setSelectedRequester(null);

    // Récupérer les demandes pour ce jeu spécifique avec plus d'informations sur les créateurs
    const { data } = await supabase
      .from("key_requests")
      .select(`
        *,
        game:games(title),
        creator:profiles!key_requests_creator_id_fkey(
          id,
          display_name,
          avatar_url,
          bio,
          platform,
          channel_url,
          subscriber_count,
          favorite_genres,
          twitch_url,
          youtube_url,
          tiktok_url,
          instagram_url,
          twitter_url,
          bluesky_url,
          steam_url,
          steam_id,
          is_verified
        )
      `)
      .eq("game_id", game.id)
      .order("created_at", { ascending: false });

    let requests = (data as unknown as KeyRequest[]) || [];

    // Pour chaque requête approved, récupérer le statut is_activated depuis la table keys
    requests = await Promise.all(
      requests.map(async (request) => {
        let is_activated: boolean | undefined = undefined;

        if (request.status === 'approved' && request.response_message) {
          // Extraire le key_code du message "Key assigned: XXXXX-XXXXX-XXXXX"
          const keyCode = request.response_message.replace('Key assigned: ', '').trim();

          if (keyCode) {
            const { data: keyDataArray } = await supabase
              .from('keys')
              .select('is_activated')
              .eq('key_code', keyCode)
              .eq('claimed_by', request.creator_id)
              .limit(1);

            const keyData = keyDataArray?.[0];

            // Explicitly set the value - if keyData exists, use is_activated (even if false)
            if (keyData !== null && keyData !== undefined) {
              is_activated = keyData.is_activated;
            }
          }
        }

        return {
          ...request,
          is_activated
        };
      })
    );

    // Load report information for each request
    if (user?.id) {
      const requestsWithReportInfo = await Promise.all(
        requests.map(async (request) => {
          const hasReported = await hasDeveloperReportedCreator(user.id, request.creator.id);
          const reportDetails = await getCreatorReportDetails(request.creator.id);
          const trustScore = await getCreatorTrustScore(request.creator.id);

          return {
            ...request,
            creator: {
              ...request.creator,
              steam_url: request.creator.steam_url || null,
              steam_id: request.creator.steam_id || null,
              is_verified: request.creator.is_verified || false,
              has_reported: hasReported,
              report_details: reportDetails,
              trust_score: trustScore
            }
          };
        })
      );

      setGameRequests(requestsWithReportInfo);

      // Sélectionner automatiquement la première requête si elle existe
      if (requestsWithReportInfo.length > 0) {
        setSelectedRequester(requestsWithReportInfo[0]);
      }
    } else {
      setGameRequests(requests);

      // Sélectionner automatiquement la première requête si elle existe
      if (requests.length > 0) {
        setSelectedRequester(requests[0]);
      }
    }
  };

  const handleViewGameDetail = (game: Game) => {
    navigate(`/game/${game.id}`);
  };

  const handleTogglePublish = async (game: Game) => {
    try {
      const { error } = await supabase
        .from('games')
        .update({ is_published: !game.is_published } as any)
        .eq('id', game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Game ${!game.is_published ? 'published' : 'unpublished'} successfully!`);
        fetchGames(); // Refresh the games list
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error('Error updating publish status');
    }
  };

  const handleToggleRequests = async (game: Game) => {
    try {
      const { error } = await supabase
        .from('games')
        .update({ allow_requests: !game.allow_requests } as any)
        .eq('id', game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Requests ${!game.allow_requests ? 'enabled' : 'disabled'} successfully!`);
        fetchGames(); // Refresh the games list
      }
    } catch (error) {
      console.error('Error toggling requests status:', error);
      toast.error('Error updating requests status');
    }
  };

  // Show loading state while checking verification
  if (isCheckingVerification) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show restriction message if not verified
  if (isVerified !== true) {
    return <AccountNotVerified />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Developer Dashboard</h1>
        <p className="text-muted-foreground">Manage your games and key requests</p>
      </div>

      <div className="grid gap-8">
        {/* Games Section */}
        <Card className="p-6 card-gradient border-border/50">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">My Games</h2>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-2">
                <Button
                  className="glow-effect"
                  onClick={() => setIsSteamSyncModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Game
                </Button>
                <Button
                  variant="outline"
                  onClick={() => syncAvailableKeys(true)}
                  disabled={isSyncingKeys}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncingKeys ? 'animate-spin' : ''}`} />
                  {isSyncingKeys ? 'Syncing...' : 'Sync Keys'}
                </Button>
              </div>
              {autoSyncCompleted && !isSyncingKeys && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span>Auto-synced</span>
                </div>
              )}
            </div>
          </div>

          {/* Steam Sync Modal */}
          <Dialog open={isSteamSyncModal} onOpenChange={setIsSteamSyncModal}>
            <DialogContent className="bg-card max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Game</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="steam_game_id_sync">Steam Game ID</Label>
                  <Input
                    id="steam_game_id_sync"
                    type="number"
                    value={steamGameIdInput}
                    onChange={(e) => setSteamGameIdInput(e.target.value)}
                    placeholder="e.g. 2448830"
                    disabled={isLoadingSteamData}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the Steam App ID to sync game data automatically
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSteamSyncModal(false);
                      setSteamGameIdInput("");
                      // Si on annule, ouvrir quand même la modal complète avec champs vides
                      setIsAddingGame(true);
                    }}
                    disabled={isLoadingSteamData}
                  >
                    Skip
                  </Button>
                  <Button
                    onClick={handleSteamSync}
                    disabled={isLoadingSteamData || !steamGameIdInput.trim()}
                  >
                    {isLoadingSteamData ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync with Steam
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Full Add Game Modal */}
          <Dialog open={isAddingGame} onOpenChange={setIsAddingGame}>
            <DialogContent className="bg-card max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Game</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddGame} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="space-y-2">
                    <Label htmlFor="title">Game Title</Label>
                    <Input
                      id="title"
                      value={newGame.title}
                      onChange={(e) => setNewGame({ ...newGame, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="genres">Genres</Label>
                      {newGame.genres.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setNewGame({ ...newGame, genres: [] })}
                          className="text-sm text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      <GenreSelector
                        value={newGame.genres}
                        onChange={(genres) => setNewGame({ ...newGame, genres })}
                        placeholder="Select genres..."
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <RichTextEditor
                    content={newGame.description}
                    onChange={(content) => setNewGame({ ...newGame, description: content })}
                    placeholder="Describe your game with rich text formatting..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cover_image">
                      Cover Image <span className="text-muted-foreground text-sm font-normal">(Recommendations: 1920x550 & Centered Logo)</span>
                    </Label>
                    <Input
                      id="cover_image"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageChange}
                      className="cursor-pointer"
                    />
                    {coverImageFile && (
                      <div className="mt-2 flex items-center gap-2">
                        <Image className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-600">{coverImageFile.name}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCoverImageFile(null)}
                        >
                          <XIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {!coverImageFile && newGame.cover_image_url && (
                      <div className="mt-2 flex items-center gap-2">
                        <Image className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-blue-600">Steam cover image loaded</span>
                        {newGame.cover_image_url && (
                          <img src={newGame.cover_image_url} alt="Cover" className="w-16 h-9 object-cover rounded border" />
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="release_date">Release Date</Label>
                    <Input
                      id="release_date"
                      type="date"
                      value={newGame.release_date}
                      onChange={(e) => setNewGame({ ...newGame, release_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* <div>
                    <Label htmlFor="highlight_id">Highlight Level</Label>
                    <select
                      id="highlight_id"
                      value={newGame.highlight_id}
                      onChange={(e) => setNewGame({ ...newGame, highlight_id: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    >
                      <option value={1}>Default (1)</option>
                      <option value={2}>Featured (2)</option>
                      <option value={3}>Premium (3)</option>
                      <option value={4}>VIP (4)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Defines how prominently your game is displayed to creators
                    </p>
                  </div> */}
                <div>
                  <Label htmlFor="screenshots">Screenshots</Label>
                  <Input
                    id="screenshots"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleScreenshotChange}
                    className="cursor-pointer"
                  />
                  {screenshotFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {screenshotFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Image className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-green-600">{file.name}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeScreenshotFile(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {screenshotFiles.length === 0 && newGame.screenshots && newGame.screenshots.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-blue-600 mb-2">Steam screenshots loaded ({newGame.screenshots.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {newGame.screenshots.slice(0, 5).map((url, index) => (
                          <img key={index} src={url} alt={`Screenshot ${index + 1}`} className="w-24 h-14 object-cover rounded border" />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="steam">Steam URL *</Label>
                    <Input
                      id="steam"
                      type="url"
                      value={newGame.steam_url}
                      onChange={(e) => setNewGame({ ...newGame, steam_url: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="steam_game_id">Steam Game ID *</Label>
                    <Input
                      id="steam_game_id"
                      type="number"
                      value={newGame.steam_game_id}
                      onChange={(e) => setNewGame({ ...newGame, steam_game_id: e.target.value })}
                      placeholder="e.g. 2448830"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Steam App ID (for playtime tracking)
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Price (€)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newGame.price}
                      onChange={(e) => setNewGame({ ...newGame, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Game price in euros (used to calculate coin cost)
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="youtube_trailer_url">YouTube Trailer URL</Label>
                    <Input
                      id="youtube_trailer_url"
                      type="url"
                      value={newGame.youtube_trailer_url || ''}
                      onChange={(e) => setNewGame({ ...newGame, youtube_trailer_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      YouTube video URL for the game trailer
                    </p>
                  </div>
                </div>

                {/* Tracking Fields Section */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-xl font-semibold text-primary">Content Creator Tracking</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hashtag">Hashtag</Label>
                      <Input
                        id="hashtag"
                        placeholder="#YourGame"
                        value={newGame.hashtag || ''}
                        onChange={(e) => setNewGame({ ...newGame, hashtag: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        For TikTok, Youtube, Instagram, and Facebook
                      </p>
                    </div>
                    <div className="hidden">
                      <Label htmlFor="youtube_tag">YouTube Tag</Label>
                      <Input
                        id="youtube_tag"
                        placeholder="yourgame"
                        value={newGame.youtube_tag || ''}
                        onChange={(e) => setNewGame({ ...newGame, youtube_tag: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        For YouTube video tags
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="twitch_category">Twitch Category</Label>
                      <Input
                        id="twitch_category"
                        placeholder="Your Game"
                        value={newGame.twitch_category || ''}
                        onChange={(e) => setNewGame({ ...newGame, twitch_category: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Category for Twitch streams
                      </p>
                    </div>
                    <div className="hidden">
                      <Label htmlFor="keymailer_hashtag">IndieKeys Hashtag</Label>
                      <Input
                        id="keymailer_hashtag"
                        placeholder="#indiekeys"
                        value={newGame.keymailer_hashtag || '#indiekeys'}
                        onChange={(e) => setNewGame({ ...newGame, keymailer_hashtag: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        For coverage identification
                      </p>
                    </div>
                  </div>
                  <div className="hidden">
                    <Label htmlFor="disclosure_text">Disclosure Text</Label>
                    <Textarea
                      id="disclosure_text"
                      placeholder="Disclosure: I received a free review copy of this product from IndieKeys"
                      value={newGame.disclosure_text || 'Disclosure: I received a free review copy of this product from IndieKeys'}
                      onChange={(e) => setNewGame({ ...newGame, disclosure_text: e.target.value })}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required disclosure text for content creators
                    </p>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <Upload className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    "Add Game"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </Card>

        <div className="grid gap-4">
          {games.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No games yet. Add your first game!
            </p>
          ) : (
            games.map((game) => (
              <Card key={game.id} className="p-4 bg-card rounded-custom-paddings">
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  {game.cover_image_url && (
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                      <img
                        src={game.cover_image_url}
                        alt={`${game.title} cover`}
                        className="w-56 h-32 object-cover rounded-custom-paddings p-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1 flex items-center min-h-[128px]">
                    <div className="flex flex-col md:flex-row lg:justify-between gap-4 w-full items-center">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold">{game.title}</h3>

                        {/* Ligne des genres */}
                        {/* <div className="flex flex-wrap gap-1 mt-2">
                            {game.genres.map((genre, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {genre}
                              </Badge>
                            ))}
                          </div> */}

                        {/* Ligne des informations */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          <div className="flex items-center gap-1 text-[12px]">
                            <Calendar className="w-3 h-3" />
                            {new Date(game.release_date || game.created_at).toLocaleDateString('fr-FR')}
                          </div>
                          {game.screenshots && game.screenshots.length > 0 && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Image className="w-3 h-3" />
                              {game.screenshots.length} screenshots
                            </Badge>
                          )}
                          {game.pending_requests > 0 && (
                            <Badge
                              variant="destructive"
                              className="flex items-center gap-1 cursor-pointer hover:bg-destructive/80 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequests(game);
                              }}
                            >
                              <Key className="w-3 h-3" />
                              {game.pending_requests} request{game.pending_requests > 1 ? 's' : ''}
                            </Badge>
                          )}
                          {game.highlight_id > 1 && (
                            <Badge
                              variant={game.highlight_id === 2 ? "default" : game.highlight_id === 3 ? "secondary" : "destructive"}
                              className="flex items-center gap-1"
                            >
                              <Star className="w-3 h-3" />
                              Level {game.highlight_id}
                              {game.highlight_end_date && (
                                <span className="text-xs">
                                  (until {new Date(game.highlight_end_date).toLocaleDateString('fr-FR')})
                                </span>
                              )}
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2 mt-4 bg-muted/30 rounded-custom-paddings w-fit px-perso-20">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewGameDetail(game)}
                            className="w-fit px-2 text-[10px] h-8"
                          >
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTogglePublish(game)}
                            className={`w-fit px-2 text-[10px] h-8 ${game.is_published
                              ? "bg-green-600 hover:bg-green-700 text-white border-green-600"
                              : "bg-red-600 hover:bg-red-700 text-white border-red-600"
                              }`}
                          >
                            {game.is_published ? "Published" : "Publish"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleRequests(game)}
                            className={`w-fit px-2 text-[10px] h-8 ${game.allow_requests
                              ? "bg-green-600 hover:bg-green-700 text-white border-green-600"
                              : "bg-red-600 hover:bg-red-700 text-white border-red-600"
                              }`}
                          >
                            {game.allow_requests ? "Requests Open" : "Allow Requests"}
                          </Button>
                          {(isAdmin || game.hasPressKit) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGeneratePressKit(game.id)}
                              disabled={isGeneratingPressKit === game.id}
                              className="w-fit px-2 text-[10px] h-8"
                            >
                              {isGeneratingPressKit === game.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <FileText className="w-3 h-3 mr-1" />
                                  Update press kit
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div
                          className="cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                          onClick={() => handleGameClick(game)}
                        >
                          <div className="flex flex-col items-end">
                            <div className="flex items-center gap-2 text-primary">
                              <Key className="w-4 h-4" />
                              <span className="font-bold">
                                {game.available_keys} <span className="text-xs">/ {game.total_keys}</span>
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              <span className="text-green-500">{game.available_creator_keys || 0} creator</span>
                              {game.available_public_keys > 0 && (
                                <span className="ml-1.5 text-blue-500">• {game.available_public_keys} public</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 underline">Click to manage</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

      </div>

      {/* Dialog pour gérer les clés */}
      <Dialog open={isManagingKeys} onOpenChange={setIsManagingKeys}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Manage Keys - {selectedGame?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-custom-paddings">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{selectedGame?.available_keys || 0}</div>
                <p className="text-sm text-muted-foreground">Available keys</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary-foreground">{selectedGame?.total_keys || 0}</div>
                <p className="text-sm text-muted-foreground">Total keys</p>
              </div>
            </div>

            <div>
              <Label htmlFor="keys">New Steam Keys</Label>
              {(() => {
                const keys = newKeys.split('\n').map(k => k.trim()).filter(k => k.length > 0);
                const invalidKeys = keys.filter(k => !isValidSteamKey(k));
                const validCount = keys.length - invalidKeys.length;
                const hasInvalidKeys = invalidKeys.length > 0;

                return (
                  <>
                    <Textarea
                      id="keys"
                      value={newKeys}
                      onChange={(e) => handleKeysChange(e.target.value)}
                      placeholder="Enter Steam keys, one per line&#10;Example:&#10;ABCD-EFGH-IJKL-MNOP&#10;QRST-UVWX-YZAB-CDEF"
                      rows={6}
                      className={`font-mono text-sm ${hasInvalidKeys ? 'border-destructive' : ''}`}
                    />
                    {!isPremium && (
                      <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
                        <p className="text-xs text-yellow-700 dark:text-yellow-400 font-medium">
                          Limited to 15 keys for creator and public, for non premium studios
                        </p>
                        {(() => {
                          const existingCount = keysForPublicPlayers ? existingPublicKeysCount : existingCreatorKeysCount;
                          const keysInField = keys.length;
                          const totalAfterAdd = existingCount + keysInField;
                          const remaining = 15 - existingCount - keysInField;

                          if (totalAfterAdd >= 15) {
                            return (
                              <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                                Limit reached: {existingCount} existing + {keysInField} new = {totalAfterAdd}/15 {keysForPublicPlayers ? 'public' : 'creator'} keys
                              </p>
                            );
                          }

                          return (
                            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                              {keysInField > 0 ? (
                                <>You have {keysInField} key(s) in the field. {remaining > 0 ? `You can add up to ${remaining} more` : 'Limit reached'} ({existingCount} existing + {keysInField} new = {totalAfterAdd}/15)</>
                              ) : (
                                <>You can add up to {15 - existingCount} more {keysForPublicPlayers ? 'public' : 'creator'} key(s) (currently have {existingCount}/15)</>
                              )}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                    {keys.length === 0 ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter one key per line. Empty lines will be ignored.
                      </p>
                    ) : hasInvalidKeys ? (
                      <div className="mt-1">
                        <p className="text-xs text-destructive font-medium">
                          {invalidKeys.length} invalid key(s) detected
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Expected format: XXXX-XXXX-XXXX or XXXX-XXXX-XXXX-XXXX (groups of 4-5 alphanumeric characters separated by hyphens)
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        ✓ {validCount} valid key(s)
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="keys-for-public"
                checked={keysForPublicPlayers}
                onCheckedChange={(checked) => {
                  setKeysForPublicPlayers(checked === true);
                  // Reset keys when switching type to re-validate
                  if (!isPremium) {
                    setNewKeys("");
                  }
                }}
              />
              <Label
                htmlFor="keys-for-public"
                className="text-sm font-normal cursor-pointer"
              >
                Keys for non-creators (public players) <br /><span className="text-xs text-muted-foreground">Submit 50 (or more) public keys and get <b>10% off</b> your game subscription</span>
              </Label>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAddKeys}
                disabled={isAddingKeys || !newKeys.trim() || !areAllKeysValid(newKeys)}
                className="flex-1"
              >
                {isAddingKeys ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Add Keys
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsManagingKeys(false)}
                disabled={isAddingKeys}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog pour voir les demandes de clés */}
      <Dialog open={isViewingRequests} onOpenChange={setIsViewingRequests}>
        <DialogContent className="bg-card max-w-6xl h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Key Requests - {selectedGame?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
            {/* Left side - Requests list (70%) */}
            <div className="flex-1 overflow-y-auto">
              {gameRequests.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No requests for this game
                </p>
              ) : (
                <div className="space-y-3">
                  {gameRequests.map((request) => (
                    <Card
                      key={request.id}
                      className={`p-4 bg-background/50 transition-colors ${selectedRequester?.id === request.id
                        ? 'ring-2 ring-primary bg-primary/5'
                        : 'hover:bg-background/70'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div
                          className="flex-1 cursor-pointer"
                          onClick={() => setSelectedRequester(request)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold">{request.creator.display_name}</h3>
                            <Badge
                              variant={
                                request.status === "approved" ? "default" :
                                  request.status === "rejected" ? "destructive" :
                                    "secondary"
                              }
                            >
                              {request.status === "approved" ? "Approved" :
                                request.status === "rejected" ? "Rejected" :
                                  "Pending"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            Date: {new Date(request.created_at).toLocaleDateString('en-US')}
                          </p>
                          {request.message && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              "{request.message}"
                            </p>
                          )}
                        </div>

                        {/* Mobile: View Profile button */}
                        <div className="lg:hidden ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedRequester(request);
                              setIsViewingCreatorProfile(true);
                            }}
                          >
                            View Profile
                          </Button>
                        </div>
                        {request.status === "pending" && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestAction(request.id, "approved");
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestAction(request.id, "rejected");
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        <div className="flex gap-2 ml-4 flex-wrap">
                          {request.creator.has_reported ? (
                            <div className="text-xs text-muted-foreground">
                              ⚠️ Reported: {request.creator.report_details?.latest_report_date ?
                                formatReportDate(request.creator.report_details.latest_report_date) :
                                'Unknown date'
                              }
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReportCreator(request.creator.id, true);
                              }}
                              className="text-xs"
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Report
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Right side - Requester profile (30%) - Desktop only */}
            <div className="hidden lg:block w-80 flex-shrink-0">
              {selectedRequester ? (
                <Card className="p-6 bg-background/50 h-full overflow-y-auto">
                  <div className="space-y-4">
                    {/* Avatar and basic info */}
                    <div className="text-center">
                      {selectedRequester.creator.avatar_url ? (
                        <img
                          src={selectedRequester.creator.avatar_url}
                          alt={selectedRequester.creator.display_name}
                          className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-muted flex items-center justify-center">
                          <span className="text-2xl font-bold">
                            {selectedRequester.creator.display_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <h3 className="text-xl font-bold">{selectedRequester.creator.display_name}</h3>
                      {selectedRequester.creator.trust_score !== undefined && (
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <Badge
                            variant="outline"
                            className={`text-sm ${getTrustScoreColor(selectedRequester.creator.trust_score)} border-current`}
                          >
                            {getTrustScoreEmoji(selectedRequester.creator.trust_score)} {getTrustScoreLabel(selectedRequester.creator.trust_score)}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Mobile: Show only basic info, hide details */}
                    <div className="lg:hidden">
                      <div className="text-center text-sm text-muted-foreground">
                        Tap to view full profile
                      </div>
                    </div>

                    {/* Desktop: Show all details */}
                    <div className="hidden lg:block space-y-4">

                      {/* Bio */}
                      {selectedRequester.creator.bio && (
                        <div>
                          <h4 className="font-semibold mb-2">Bio</h4>
                          <p className="text-sm text-muted-foreground">
                            {selectedRequester.creator.bio.length > 250
                              ? `${selectedRequester.creator.bio.substring(0, 250)}...`
                              : selectedRequester.creator.bio
                            }
                          </p>
                        </div>
                      )}

                      {/* Channel link */}
                      {selectedRequester.creator.channel_url && (
                        <div>
                          <h4 className="font-semibold mb-2">Main Channel</h4>
                          <a
                            href={selectedRequester.creator.channel_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline break-all"
                          >
                            {selectedRequester.creator.channel_url}
                          </a>
                        </div>
                      )}

                      {/* Social Media Channels */}
                      {(selectedRequester.creator.twitch_url || selectedRequester.creator.youtube_url || selectedRequester.creator.tiktok_url || selectedRequester.creator.instagram_url || selectedRequester.creator.twitter_url || selectedRequester.creator.bluesky_url || selectedRequester.creator.steam_url) ? (
                        <div>
                          <h4 className="font-semibold mb-2">Channels & Stats</h4>
                          <div className="space-y-3">
                            {/* Twitch */}
                            {selectedRequester.creator.twitch_url && (
                              <div className="flex items-center justify-between p-2 bg-purple-500/10 border border-purple-500/20 rounded-custom-paddings">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                  </svg>
                                  <div>
                                    <a
                                      href={selectedRequester.creator.twitch_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-medium text-purple-600 hover:text-purple-700 hover:underline text-sm"
                                    >
                                      Twitch
                                    </a>
                                    {(selectedRequester.creator.twitch_followers_count || selectedRequester.creator.twitch_subscribers_count || selectedRequester.creator.twitch_views_count) && (
                                      <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                                        {selectedRequester.creator.twitch_followers_count && selectedRequester.creator.twitch_followers_count > 0 && (
                                          <span>{selectedRequester.creator.twitch_followers_count.toLocaleString()} followers</span>
                                        )}
                                        {selectedRequester.creator.twitch_subscribers_count && selectedRequester.creator.twitch_subscribers_count > 0 && (
                                          <span>{selectedRequester.creator.twitch_subscribers_count.toLocaleString()} subscribers</span>
                                        )}
                                        {selectedRequester.creator.twitch_views_count && selectedRequester.creator.twitch_views_count > 0 && (
                                          <span>{selectedRequester.creator.twitch_views_count.toLocaleString()} views</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* YouTube */}
                            {selectedRequester.creator.youtube_url && (
                              <div className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/20 rounded-custom-paddings">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                  </svg>
                                  <div>
                                    <a
                                      href={selectedRequester.creator.youtube_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-medium text-red-600 hover:text-red-700 hover:underline text-sm"
                                    >
                                      YouTube
                                    </a>
                                    {(selectedRequester.creator.youtube_subscribers_count || selectedRequester.creator.youtube_views_count) && (
                                      <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                                        {selectedRequester.creator.youtube_subscribers_count && selectedRequester.creator.youtube_subscribers_count > 0 && (
                                          <span>{selectedRequester.creator.youtube_subscribers_count.toLocaleString()} subscribers</span>
                                        )}
                                        {selectedRequester.creator.youtube_views_count && selectedRequester.creator.youtube_views_count > 0 && (
                                          <span>{selectedRequester.creator.youtube_views_count.toLocaleString()} views</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* TikTok */}
                            {selectedRequester.creator.tiktok_url && (
                              <div className="flex items-center justify-between p-2 bg-pink-500/10 border border-pink-500/20 rounded-custom-paddings">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                  </svg>
                                  <div>
                                    <a
                                      href={selectedRequester.creator.tiktok_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-medium text-pink-600 hover:text-pink-700 hover:underline text-sm"
                                    >
                                      TikTok
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Other social media */}
                            <div className="flex flex-wrap gap-2">
                              {selectedRequester.creator.instagram_url && (
                                <a
                                  href={selectedRequester.creator.instagram_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-custom-paddings hover:opacity-90 transition-opacity"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                  </svg>
                                  Instagram
                                </a>
                              )}
                              {selectedRequester.creator.twitter_url && (
                                <a
                                  href={selectedRequester.creator.twitter_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-black text-white rounded-custom-paddings hover:bg-gray-800 transition-colors"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                  </svg>
                                  X
                                </a>
                              )}
                              {selectedRequester.creator.bluesky_url && (
                                <a
                                  href={selectedRequester.creator.bluesky_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-sky-500 text-white rounded-custom-paddings hover:bg-sky-600 transition-colors"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 10.8c-1.087-2.183-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.22-.254.455-.345.705-.18.5-.275 1.03-.275 1.58 0 1.418.488 2.73 1.375 3.78.89 1.05 2.205 1.65 3.625 1.65s2.735-.6 3.625-1.65c.887-1.05 1.375-2.362 1.375-3.78 0-.55-.095-1.08-.275-1.58-.091-.25-.207-.485-.345-.705.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.79.624-6.478 0-.69-.139-2.861-.902-3.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.617 12 10.8z" />
                                  </svg>
                                  Bluesky
                                </a>
                              )}
                              {selectedRequester.creator.steam_url && (
                                <a
                                  href={selectedRequester.creator.steam_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-3 text-sm font-medium bg-gradient-to-r from-[#2a475e] to-[#3c5a6b] text-white rounded-custom-paddings hover:from-[#3c5a6b] hover:to-[#4a6b7a] transition-all duration-200 shadow-lg hover:shadow-xl border border-[#4a6b7a] ring-2 ring-[#2a475e]/20"
                                >
                                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                                  </svg>
                                  Steam
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-semibold mb-2">Channels</h4>
                          <p className="text-sm text-muted-foreground">No social media channels available</p>
                        </div>
                      )}

                      {/* Favorite genres */}
                      {selectedRequester.creator.favorite_genres && (
                        <div>
                          <h4 className="font-semibold mb-2">Favorite Genres</h4>
                          <div className="flex flex-wrap gap-1">
                            {parseGenres(selectedRequester.creator.favorite_genres).map((genre, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {genre}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Request details */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-2">Request Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium">Date:</span> {new Date(selectedRequester.created_at).toLocaleDateString('en-US')}
                          </div>
                          <div>
                            <span className="font-medium">Status:</span>
                            <Badge
                              variant={
                                selectedRequester.status === "approved" ? "default" :
                                  selectedRequester.status === "rejected" ? "destructive" :
                                    "secondary"
                              }
                              className="ml-2"
                            >
                              {selectedRequester.status === "approved" ? "Approved" :
                                selectedRequester.status === "rejected" ? "Rejected" :
                                  "Pending"}
                            </Badge>
                          </div>
                        </div>


                        {selectedRequester.response_message && (
                          <div className="mt-3 p-3 bg-primary/10 rounded-md">
                            <p className="text-sm font-medium mb-1">Response:</p>
                            <p className="text-sm">"{selectedRequester.response_message}"</p>
                          </div>
                        )}

                        {/* Report Creator */}
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold mb-3">Creator Management</h4>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReportCreator(selectedRequester.creator.id, true)}
                              className="text-xs"
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Report
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div> {/* End desktop details */}
                  </div>
                </Card>
              ) : (
                <Card className="p-6 bg-background/50 h-full flex items-center justify-center">
                  <p className="text-muted-foreground text-center">
                    Select a request to view creator details
                  </p>
                </Card>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Creator Profile Modal */}
      <Dialog open={isViewingCreatorProfile} onOpenChange={setIsViewingCreatorProfile}>
        <DialogContent className="bg-card max-w-md h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Creator Profile</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {selectedRequester ? (
              <div className="space-y-6">
                {/* Avatar and basic info */}
                <div className="text-center">
                  {selectedRequester.creator.avatar_url ? (
                    <img
                      src={selectedRequester.creator.avatar_url}
                      alt={selectedRequester.creator.display_name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-muted flex items-center justify-center">
                      <span className="text-3xl font-bold">
                        {selectedRequester.creator.display_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{selectedRequester.creator.display_name}</h3>
                  {selectedRequester.creator.trust_score !== undefined && (
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Badge
                        variant="outline"
                        className={`text-sm ${getTrustScoreColor(selectedRequester.creator.trust_score)} border-current`}
                      >
                        {getTrustScoreEmoji(selectedRequester.creator.trust_score)} {getTrustScoreLabel(selectedRequester.creator.trust_score)}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Bio */}
                {selectedRequester.creator.bio && (
                  <div>
                    <h4 className="font-semibold mb-3 text-lg">Bio</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {selectedRequester.creator.bio.length > 250
                        ? `${selectedRequester.creator.bio.substring(0, 250)}...`
                        : selectedRequester.creator.bio
                      }
                    </p>
                  </div>
                )}

                {/* Main Channel */}
                {selectedRequester.creator.channel_url && (
                  <div>
                    <h4 className="font-semibold mb-3 text-lg">Main Channel</h4>
                    <a
                      href={selectedRequester.creator.channel_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline break-all block"
                    >
                      {selectedRequester.creator.channel_url}
                    </a>
                  </div>
                )}

                {/* Social Media Channels */}
                {(selectedRequester.creator.twitch_url || selectedRequester.creator.youtube_url || selectedRequester.creator.tiktok_url || selectedRequester.creator.instagram_url || selectedRequester.creator.twitter_url || selectedRequester.creator.bluesky_url || selectedRequester.creator.steam_url) ? (
                  <div>
                    <h4 className="font-semibold mb-3 text-lg">Channels & Stats</h4>
                    <div className="space-y-4">
                      {/* Twitch */}
                      {selectedRequester.creator.twitch_url && (
                        <div className="flex items-center justify-between p-2 bg-purple-500/10 border border-purple-500/20 rounded-custom-paddings">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                            </svg>
                            <div>
                              <a
                                href={selectedRequester.creator.twitch_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-purple-600 hover:text-purple-700 hover:underline text-sm"
                              >
                                Twitch
                              </a>
                              {(selectedRequester.creator.twitch_followers_count || selectedRequester.creator.twitch_subscribers_count || selectedRequester.creator.twitch_views_count) && (
                                <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                                  {selectedRequester.creator.twitch_followers_count && selectedRequester.creator.twitch_followers_count > 0 && (
                                    <span>{selectedRequester.creator.twitch_followers_count.toLocaleString()} followers</span>
                                  )}
                                  {selectedRequester.creator.twitch_subscribers_count && selectedRequester.creator.twitch_subscribers_count > 0 && (
                                    <span>{selectedRequester.creator.twitch_subscribers_count.toLocaleString()} subscribers</span>
                                  )}
                                  {selectedRequester.creator.twitch_views_count && selectedRequester.creator.twitch_views_count > 0 && (
                                    <span>{selectedRequester.creator.twitch_views_count.toLocaleString()} views</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* YouTube */}
                      {selectedRequester.creator.youtube_url && (
                        <div className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/20 rounded-custom-paddings">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                            <div>
                              <a
                                href={selectedRequester.creator.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-red-600 hover:text-red-700 hover:underline text-sm"
                              >
                                YouTube
                              </a>
                              {(selectedRequester.creator.youtube_subscribers_count || selectedRequester.creator.youtube_views_count) && (
                                <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                                  {selectedRequester.creator.youtube_subscribers_count && selectedRequester.creator.youtube_subscribers_count > 0 && (
                                    <span>{selectedRequester.creator.youtube_subscribers_count.toLocaleString()} subscribers</span>
                                  )}
                                  {selectedRequester.creator.youtube_views_count && selectedRequester.creator.youtube_views_count > 0 && (
                                    <span>{selectedRequester.creator.youtube_views_count.toLocaleString()} views</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* TikTok */}
                      {selectedRequester.creator.tiktok_url && (
                        <div className="flex items-center justify-between p-2 bg-pink-500/10 border border-pink-500/20 rounded-custom-paddings">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                            </svg>
                            <div>
                              <a
                                href={selectedRequester.creator.tiktok_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-pink-600 hover:text-pink-700 hover:underline text-sm"
                              >
                                TikTok
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Other social media */}
                      <div className="flex flex-wrap gap-2">
                        {selectedRequester.creator.instagram_url && (
                          <a
                            href={selectedRequester.creator.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-custom-paddings hover:opacity-90 transition-opacity"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                            Instagram
                          </a>
                        )}
                        {selectedRequester.creator.twitter_url && (
                          <a
                            href={selectedRequester.creator.twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-black text-white rounded-custom-paddings hover:bg-gray-800 transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            X
                          </a>
                        )}
                        {selectedRequester.creator.bluesky_url && (
                          <a
                            href={selectedRequester.creator.bluesky_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-sky-500 text-white rounded-custom-paddings hover:bg-sky-600 transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 10.8c-1.087-2.183-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.22-.254.455-.345.705-.18.5-.275 1.03-.275 1.58 0 1.418.488 2.73 1.375 3.78.89 1.05 2.205 1.65 3.625 1.65s2.735-.6 3.625-1.65c.887-1.05 1.375-2.362 1.375-3.78 0-.55-.095-1.08-.275-1.58-.091-.25-.207-.485-.345-.705.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.79.624-6.478 0-.69-.139-2.861-.902-3.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.617 12 10.8z" />
                            </svg>
                            Bluesky
                          </a>
                        )}
                        {selectedRequester.creator.steam_url && (
                          <a
                            href={selectedRequester.creator.steam_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#1b2838] text-white rounded-custom-paddings hover:bg-[#2a475e] transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                            </svg>
                            Steam
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="font-semibold mb-3 text-lg">Channels</h4>
                    <p className="text-sm text-muted-foreground">No social media channels available</p>
                  </div>
                )}

                {/* Favorite Genres */}
                {selectedRequester.creator.favorite_genres && (
                  <div>
                    <h4 className="font-semibold mb-3 text-lg">Favorite Genres</h4>
                    <div className="flex flex-wrap gap-2">
                      {parseGenres(selectedRequester.creator.favorite_genres).map((genre) => (
                        <Badge key={genre} variant="secondary" className="text-xs">
                          {genre}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Request Details */}
                <div className="border-t pt-6">
                  <h4 className="font-semibold mb-3 text-lg">Request Details</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Date:</span> {new Date(selectedRequester.created_at).toLocaleDateString('en-US')}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>
                      <Badge
                        variant={
                          selectedRequester.status === "approved" ? "default" :
                            selectedRequester.status === "rejected" ? "destructive" :
                              "secondary"
                        }
                        className="ml-2"
                      >
                        {selectedRequester.status === "approved" ? "Approved" :
                          selectedRequester.status === "rejected" ? "Rejected" :
                            "Pending"}
                      </Badge>
                    </div>
                  </div>

                  {selectedRequester.message && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-custom-paddings">
                      <p className="text-sm font-medium mb-2">Message:</p>
                      <p className="text-sm">"{selectedRequester.message}"</p>
                    </div>
                  )}

                  {selectedRequester.response_message && (
                    <div className="mt-4 p-4 bg-primary/10 rounded-custom-paddings">
                      <p className="text-sm font-medium mb-2">Response:</p>
                      <p className="text-sm">"{selectedRequester.response_message}"</p>
                    </div>
                  )}

                  {/* Report Creator */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 text-lg">Creator Management</h4>
                    <div className="flex justify-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReportCreator(selectedRequester.creator.id, true)}
                        className="text-xs"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Report
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground text-center">
                  No creator selected
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
